import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Inbox, Hourglass, ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import GolfWidget from "@/components/GolfWidget";
import { PRIORITY_META, fmtDeadline, fmtDur } from "@/lib/ui";

export default function Today() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/brief/today").then((r) => setBrief(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="h-[60vh] flex items-center justify-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const today = new Date(brief.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="space-y-6">
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{today}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1" data-testid="today-greeting">
          {brief.greeting}{brief.name ? `, ${brief.name}` : ""}.
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        {/* Hero: top 3 */}
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="md:col-span-8 rounded-2xl bg-gradient-to-br from-card to-card/50 border border-border p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-lg font-semibold">Les 3 choses qui comptent aujourd'hui</h2>
            <Link to="/tasks" data-testid="today-all-tasks" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              Toutes les tâches <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3" data-testid="today-top3">
            {brief.top3.length === 0 && <div className="text-sm text-muted-foreground">Rien d'urgent. Profites-en.</div>}
            {brief.top3.map((t, i) => {
              const p = PRIORITY_META[t.priority] || PRIORITY_META.CAN_WAIT;
              return (
                <div key={t.id} className="flex items-start gap-4 rounded-xl border border-border p-4 hover:border-primary/40 transition-colors">
                  <div className="font-display text-2xl font-semibold text-muted-foreground/40 w-6">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{t.title}</div>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full border ${p.cls}`}>{p.label}</span>
                      {t.project && t.project !== "Autre" && <span className="text-xs text-muted-foreground">{t.project}</span>}
                      {t.deadline && <span className="text-xs text-muted-foreground">· échéance {fmtDeadline(t.deadline)}</span>}
                      {t.estimated_minutes && <span className="text-xs text-muted-foreground">· {fmtDur(t.estimated_minutes)}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.section>

        {/* Golf */}
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="md:col-span-4 rounded-2xl bg-card border border-border p-6">
          <GolfWidget compact />
        </motion.section>

        {/* Calendar */}
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="md:col-span-5 rounded-2xl bg-card border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-primary" />
            <h2 className="font-display text-base font-semibold">Agenda du jour</h2>
          </div>
          <div className="space-y-2" data-testid="today-calendar">
            {brief.calendar.length === 0 && <div className="text-sm text-muted-foreground">Aucun rendez-vous. Journée ouverte.</div>}
            {brief.calendar.map((e) => (
              <div key={e.id} className="flex items-center gap-3 rounded-lg border-l-2 border-l-sky-500 bg-sky-500/5 px-3 py-2.5">
                <div className="font-mono text-sm text-sky-300 w-14">{e.start}</div>
                <div className="text-sm truncate">{e.summary}</div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Waiting for */}
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="md:col-span-4 rounded-2xl bg-card border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Hourglass className="w-4 h-4 text-amber-400" />
            <h2 className="font-display text-base font-semibold">En attente de…</h2>
          </div>
          <div className="space-y-2.5" data-testid="today-waiting">
            {brief.waiting_for.length === 0 && <div className="text-sm text-muted-foreground">Tu n'attends rien de personne.</div>}
            {brief.waiting_for.map((w) => (
              <div key={w.id} className="text-sm">
                <div className="text-foreground">{w.summary}</div>
                {w.from && <div className="text-xs text-muted-foreground">{w.from}</div>}
              </div>
            ))}
          </div>
        </motion.section>

        {/* Inbox + personal time */}
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="md:col-span-3 rounded-2xl bg-card border border-border p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3"><Inbox className="w-4 h-4 text-primary" /><h2 className="font-display text-base font-semibold">Inbox</h2></div>
            <div className="font-display text-4xl font-semibold" data-testid="today-inbox-count">{brief.inbox_count}</div>
            <div className="text-sm text-muted-foreground">message(s) demandent ton attention.</div>
            {brief.account_breakdown && brief.account_breakdown.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="today-account-breakdown">
                {brief.account_breakdown.map((b) => (
                  <span key={b.type} className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    {b.count} {{ WORK: "Pro", WORK_LEGACY: "Pro (ancien)", PERSONAL: "Perso", OTHER: "Autre" }[b.type] || b.type}
                  </span>
                ))}
              </div>
            )}
          </div>
          <Link to="/inbox" data-testid="today-open-inbox" className="mt-4 text-xs text-primary hover:underline inline-flex items-center gap-1">
            Ouvrir l'inbox <ArrowUpRight className="w-3 h-3" />
          </Link>
        </motion.section>

        {/* Personal time banner */}
        {brief.personal_time && (
          <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            className="md:col-span-12 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            <div className="text-sm text-emerald-100" data-testid="today-personal-time">{brief.personal_time}</div>
          </motion.section>
        )}
      </div>
    </div>
  );
}
